import { useCallback, useEffect, useState } from "react";
import { getProject, updateProject } from "../api/projects";
import { uploadProjectLogo } from "../api/storage";

const EMPTY_PROJECT = {
  id: null,
  title: "",
  customer: "",
  startAt: null,
  disconnectLocation: "",
  connectLocation: "",
  rig: "",
  vessel: "",
  logoUrl: "",
};

const useProject = (projectId, userId, onProjectUpdated) => {
  const [project, setProject] = useState(EMPTY_PROJECT);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!projectId) {
      setProject(EMPTY_PROJECT);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getProject(projectId);
        if (!cancelled) setProject(data);
      } catch (err) {
        if (!cancelled) setError(err.message || "Failed to load project");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [projectId]);

  const handleProjectChange = useCallback((field, value) => {
    setProject((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleSaveProject = useCallback(async () => {
    if (!projectId) return null;
    if (!project.title) {
      setError("Project title is required.");
      return null;
    }

    setSaving(true);
    setError("");
    try {
      const updated = await updateProject(projectId, project);
      setProject(updated);
      onProjectUpdated?.(updated);
      return updated;
    } catch (err) {
      setError(err.message || "Failed to save project");
      return null;
    } finally {
      setSaving(false);
    }
  }, [projectId, project, onProjectUpdated]);

  const handleLogoUpload = useCallback(async (file) => {
    if (!projectId) return;
    try {
      const publicUrl = await uploadProjectLogo({ userId, projectId, file });
      const updated = await updateProject(projectId, { ...project, logoUrl: publicUrl });
      setProject(updated);
      onProjectUpdated?.(updated);
    } catch (err) {
      setError(err.message || "Failed to upload logo");
    }
  }, [projectId, userId, project, onProjectUpdated]);

  return {
    project,
    loading,
    saving,
    error,
    setError,
    handleProjectChange,
    handleSaveProject,
    handleLogoUpload,
  };
};

export { EMPTY_PROJECT };
export default useProject;
